import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { Spinner, Select, Input } from '../../../components';
import { AuthService, GeneralService, PurchasesService } from '../../../services';
import { classNames, delay, fromPhotos, sanitizeToNumbers, clean, clone, Constants, Alert, Globals } from '../../../utils';
import { ChevronRightIcon, UploadFileIcon, PDFIcon, CloseIcon, RestartIcon } from '../../../assets/icons';
import { DocUploadItem as DocUploadDocument } from '../payment/complete-vehicle-data';

import moment from 'moment';

const STRING_CEDULA = 'Cédula de Identidad';

const ORIGINS = {
  ANDROID: 1,
  IOS: 2,
  WEB: 3
}

const target = 'doc-upload-modal';

export const DocUpload = ({ onClose, onSubmit, setIdentityToken, identityToken }) => {
  const user = useSelector(state => state.user);
  const request = useSelector(state => state.request);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [visibleVehicleData, setVisibleVehicleData] = useState(false);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [genders, setGenders] = useState([]);
  const [states, setStates] = useState([]);
  const [townships, setTownships] = useState([]);
  const [cities, setCities] = useState([]);
  const [parishes, setParishes] = useState([]);
  const [dataPic, setDataPic] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [contactPhone, setContactPhone] = useState(null);
  const [maxWeight, setMaxWeight] = useState(null);
  const [selectCategories, setSelectCategories] = useState(false);
  const [attemptsAvailable, setAttemptsAvailable] = useState(null);
  const [form, setForm] = useState({
    dniFile: null,
    dniFileBlob: null,
    fiscalDniFile: null,
    fiscalDniFileBlob: null,
    gender_id: '',
    dniType: '',
    dni: '',
    birthdate: '',
    address: '',
    codestado: '',
    name_estado: '',
    codciudad: '',
    name_ciudad: '',
    codmunicipio: '',
    name_municipio: '',
    parish_id: '',
  });
  const [formVehicle, setFormVehicle] = useState({ 
    brand: '',
    model: '',
    year: '',
    vehicle_type: '',
    serial: '',
    plate: '',
    engine_serial: '',
    mileage: '100',
    color: '',
    weight: '',
    category: ''
  });

  useEffect(() => {
    const getSelects = async () => {
      Promise.all([
        GeneralService.getDocumentTypes(),
        GeneralService.getGenders(),
        getAcselStates(),
        GeneralService.getContactPhone(),
      ])
        .then(res => {
          setDocumentTypes(res[0] ?? []);
          setGenders(res[1].map(x => ({ value: x.id, label: x.name })) ?? []);
          setStates(res[2]);
          setContactPhone(res[3].contact_phone);
          setMaxWeight(res[3].max_weight);
        })
        .catch(console.log);
    }
    getSelects();
  },[]);

  const getTownships = async (codestado) => {
    if (!codestado) return;
    GeneralService.getTownships({ codestado })
      .then(res => {
        setTownships(res.map(x => ({ value: x.id, label: x.name })));
      })
      .catch(console.log);
  }

  const getAcselStates = async () => {
    const data = { route: 'estados', codpais: '001' };
    const res = await GeneralService.acxelCallApi(data);
    return res.map(x => ({ value: x.id, label: x.name }));
  };

  const getAcselCities = async (codestado) => {
    if (!codestado) return;
    const data = {
      route: 'ciudades',
      codpais: '001',
      codestado
    }
    GeneralService.acxelCallApi(data)
      .then(res => {
        setCities(res.map(x => ({ value: x.value, label: x.label })));
      })
      .catch(console.log);
  }

  const getAcselTownships = async (codciudad) => {
    if (!codciudad) return;
    const data = {
      route: 'municipios',
      codpais: '001',
      codestado: form.codestado,
      codciudad: codciudad,
      filter_my_townships: true,
    }

    GeneralService.acxelCallApi(data)
      .then(res => {
        setTownships(res.map(x => ({ value: x.id, label: x.name })));
      })
      .catch(console.log);
  }

  const getParishes = async (codmunicipio) => {
    if (!codmunicipio) return;
    GeneralService.getParishes({ codmunicipio })
      .then(res => {
        setParishes(res.map(x => ({ value: x.id, label: x.name })));
      })
      .catch(console.log);
  }

  const selectFile = async (file, target) => {
    setForm(s => ({
      ...s,
      [target]: file,
      [`${target}Blob`]: URL.createObjectURL(file),
    }));
  };

  const uploadDoc = async (file, prodIdx, docIdx, benefIdx) => {
    const items = request.products;
    items[prodIdx].beneficiaries[benefIdx].required_docs[docIdx].file = file;
    items[prodIdx].beneficiaries[benefIdx].required_docs[docIdx].fileBlob = URL.createObjectURL(file)
    dispatch({ type: 'SET_REQUEST', payload: { ...request, products: items } });
  };

  const isValidForm = (showError = true) => {
    const onError = (msg) => {
      if (showError) toast.error(msg);
      return false;
    };

    const dniFile = user.dni_file || form.dniFile;
    const fiscalDniFile = user.fiscal_dni_file || form.fiscalDniFile;
    if (!dniFile && !fiscalDniFile)
      return onError('Debe subir al menos uno de los documentos');

    if (!form.gender_id && !user.gender_id)
      return onError('El campo sexo es obligatorio');

    if (!form.dniType && !user.dni)
      return onError('El tipo de documento es obligatorio');

    if (!form.dni && !user.dni)
      return onError('El número de documento es obligatorio');

    if (!form.birthdate && !user.birthdate)
      return onError('La fecha de nacimiento es obligatoria');

    if (!form.address && !user.address)
      return onError('La dirección es obligatoria');

    if (!form.codestado && !user.codestado)
      return onError('El estado es obligatorio');

    if (!form.codciudad && !user.codciudad)
      return onError('La ciudad es obligatoria');

    if (!form.codmunicipio && !user.codmunicipio)
      return onError('El municipio es obligatorio');

    // if (!form.parish_id && !user.parish_id)
    //   return onError('La parroquia es obligatoria');

    const someProdWithDocsNotUploaded = request.products?.some(x => {
      return x.beneficiaries.some(b => {
        return b.required_docs?.some(d => (!d?.is_optional || d.bravealo) && !d?.file) || false;
      }) || false;
    });

    if (someProdWithDocsNotUploaded)
      return onError('Debe subir todos los documentos requeridos');

    return true;
  };

  const submit = async () => {
    if (isLoading) return;
    if (!isValidForm()) return;
    setIsLoading(true);

    try {
      if (user.birthdate || form.birthdate) {
        const ageOld = moment().diff(moment(user.birthdate || form.birthdate), 'years');
        if (ageOld < 18) {
          Alert.error('De acuerdo con nuestras políticas, solo podemos ofrecer nuestros servicios a personas mayores de 18 años. Por favor, verifica tu edad antes de continuar', target);
          throw new Error('Error de edad');
        }
      }

      if ((!user.dni_file && !user.fiscal_dni_file) ||
         !user.gender_id ||
         !user.dni ||
         !user.birthdate ||
         !user.address ||
         !user.codestado ||
         !user.codciudad ||
         !user.codmunicipio) {
        const data = { ...form, hasFile: true, birthdate: form.birthdate ? moment(form.birthdate).format('YYYY-MM-DD') : '' };
        const res = await AuthService.uploadDniFiles(data);
        dispatch({ type: 'SET_USER', payload: res });
      }

      dispatch({ type: 'SET_REQUEST', payload: { ...request, isDocUploaded: true } });
      await delay(1);
      onClose();
      onSubmit();

    } catch (error) {
      //
    }
    setIsLoading(false);
  };

  const getData = async (file) => {
    setIsLoadingData(true);

    try {
      let identity_token = identityToken;
			// Se comenta la función ya que genera un error debido a que Netuno tiene bloqueada la página
      // const response = await fetch('https://api.ipify.org?format=json');
      // const data = await response.json();

      if (!identityToken) {
        const res = await PurchasesService.faceIdentityToken();
        if (res.face_identity_token) {
          setIdentityToken(res.face_identity_token);
          identity_token = res.face_identity_token;
        }
        if (res.attempts_available !== null) {
          setAttemptsAvailable(res.attempts_available);
        }
        if (res.vehicle_data) {
          setFormVehicle(s => ({ 
            ...s,
            brand: res.vehicle_data?.brand || '',
            model: res.vehicle_data?.model || '',
            year: res.vehicle_data?.year?.toString() || '',
            vehicle_type: res.vehicle_data?.type || '',
            serial: res.vehicle_data?.serial || '',
            plate: res.vehicle_data?.plate || '',
            engine_serial: res.vehicle_data?.serial || '',
            color: res.vehicle_data?.color || '',
            weight: res.vehicle_data?.weight || '',
            category: res.vehicle_data?.category || ''
          }));
        }
      }

      const res = await PurchasesService.verifyVehicleCard({
        hasFile: true,
        origin_id: ORIGINS.WEB,
        document_photo: file,
        ip: null,
        face_identity_token: identity_token
      });
      if (res.attempts_available !== null) {
        setAttemptsAvailable(res.attempts_available);
      }
      if (res.vehicle_data) {
        //Valido si la categoria del vehiculo del carnet, coincide con el tipo de plan seleccionado
        const validationError = validateVehicleCategory(res.vehicle_data);
        if (validationError) {
          return validationError;
        }
        
        setFormVehicle(s => ({ 
          ...s,
          brand: res.vehicle_data?.brand || '',
          model: res.vehicle_data?.model || '',
          year: res.vehicle_data?.year?.toString() || '',
          vehicle_type: res.vehicle_data?.type || '',
          serial: res.vehicle_data?.serial || '',
          plate: res.vehicle_data?.plate || '',
          engine_serial: res.vehicle_data?.serial || '',
          color: res.vehicle_data?.color || '',
          weight: res.vehicle_data?.weight || '',
          category: res.vehicle_data?.category || ''
        }));
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingData(false);
    }
  }

  const validateVehicleCategoryOLD = (vehicle_data) => {
    console.log('vehicle_data: ', vehicle_data)
    const product = request?.products?.[0];
    if (!product) return true;

    const selectedPrice = product.product?.prices?.find(
      price => price.id === product.product.selectedPriceId
    );
    console.log('selectedPrice: ', selectedPrice)

    if (!selectedPrice) {
      Alert.error("No se encontró el plan seleccionado", target);
      return true;
    }

    setSelectCategories(false);

    // Validación cuando existe categoría
    if (vehicle_data?.category) {
      // Encontrar todas las categorías permitidas para el plan seleccionado
      const priceCategories = selectedPrice.categories?.map(item => item.name) || [];
      console.log('priceCategories: ', priceCategories)

      // Función de normalización con manejo de valores undefined/null
      const normalizeString = (str) => {
        if (!str) return ''; // Manejo de valores nulos/undefined
        return clean(str.toString()) // Asegurar que es string
          .toLowerCase()
          .replace(/\s+/g, ' ')
          .trim();
      };

      const normalizedVehicleCategory = normalizeString(vehicle_data.category);

      // Verificar contra todas las categorías permitidas
      const isValid = priceCategories.some(category => {
        return normalizeString(category) === normalizedVehicleCategory;
      });

      if (!isValid) {
        Alert.error("El tipo de vehículo en el documento no corresponde con el plan seleccionado. Intenta nuevamente.",target);
        return true;
      }

      const min = selectedPrice?.carga_min || null;
      const max = selectedPrice?.carga_max || null;

      const { weight } = formVehicle;

      if (maxWeight != null && weight != '' && parseFloat(weight) >= parseFloat(maxWeight)) {
        Alert.error("Debe dirigirse a una de nuestras oficinas para la Emisión de su póliza" + (contactPhone != null ? ` y/o llamar o escribir al número ${ contactPhone }` : ""),target);
        return true;
      }

      const isWeightValid = (min == null || weight >= min) && (max == null || weight <= max);

      if (!isWeightValid) {
        Alert.error("La capacidad de carga del vehículo en el documento no corresponde con el plan seleccionado. Intenta nuevamente.",target);
        return true;
      }
    }
    // Validación cuando NO existe categoría
    else {
      setSelectCategories(true);
    }

    return null; // No hay errores
  };

  const validateVehicleCategory = (vehicle_data) => {
    // 1. Obtención y validación inicial del plan
    const product = request?.products?.[0];
    if (!product) return true;

    const selectedPrice = product.product?.prices?.find(
      price => price.id === product.product.selectedPriceId
    );

    if (!selectedPrice) {
      Alert.error("No se encontró el plan seleccionado", target);
      return true;
    }

    setSelectCategories(false);

    // 2. Validación de Categoría
    if (vehicle_data?.category) {
      const priceCategories = selectedPrice.categories?.map(item => item.name) || [];

      const normalizeString = (str) => {
        if (!str) return '';
        return clean(str.toString()).toLowerCase().replace(/\s+/g, ' ').trim();
      };

      const normalizedVehicleCategory = normalizeString(vehicle_data.category);

      const isValid = priceCategories.some(category => {
        return normalizeString(category) === normalizedVehicleCategory;
      });

      if (!isValid) {
        // UX: Mensaje de error más descriptivo
        Alert.error("El tipo de vehículo en el documento no corresponde con el plan seleccionado. Intenta nuevamente.", target);
        return true;
      }

      // 3. Validación de Peso (Refactorizada con tipado estricto y Fallback)

      // ESTRATEGIA DE FALLBACK: Si formVehicle.weight está vacío, usamos vehicle_data.weight
      // Aseguramos que formVehicle.weight sea un string antes de usar trim()
      const rawWeight = (formVehicle?.weight && String(formVehicle.weight).trim() !== '')
        ? formVehicle.weight
        : vehicle_data?.weight;

      // Usamos parseFloat porque los pesos podrían venir con decimales (ej. 995.5)
      const weightVal = parseFloat(rawWeight);

      console.log('>>>> Datos de Peso -> formVehicle:', formVehicle?.weight, '| vehicle_data:', vehicle_data?.weight, '| rawWeight usado:', rawWeight, '| parseado:', weightVal);

      // Usamos Nullish Coalescing (??) en lugar de OR (||) para permitir el valor 0 si existiera
      const min = selectedPrice?.carga_min ?? null;
      const max = selectedPrice?.carga_max ?? null;

      // A. Regla de negocio: Exceso de peso general que requiere ir a oficina
      if (maxWeight != null && !isNaN(weightVal) && weightVal >= parseFloat(maxWeight)) {
        const phoneMsg = contactPhone ? ` y/o llamar o escribir al número ${contactPhone}` : "";
        Alert.error(`Debe dirigirse a una de nuestras oficinas para la Emisión de su póliza${phoneMsg}`, target);
        return true;
      }

      // B. Regla de negocio: Límites específicos del plan seleccionado
      const isWeightValid = () => {
        // Si el peso no es un número válido y existen límites requeridos, bloqueamos preventivamente
        if (isNaN(weightVal) && (min !== null || max !== null)) return false;

        const meetsMin = min === null || weightVal >= parseFloat(min);
        const meetsMax = max === null || weightVal <= parseFloat(max);

        return meetsMin && meetsMax;
      };

      if (!isWeightValid()) {
        // UX: Aplicación de Heurísticas de Nielsen (Reconocer y diagnosticar errores con datos exactos)
        Alert.error(`La capacidad de carga del vehículo (${isNaN(weightVal) ? 'No especificada' : weightVal + 'kg'}) está fuera de los límites del plan (Min: ${min !== null ? min : 'N/A'}kg, Max: ${max !== null ? max : 'N/A'}kg).`, target);
        return true;
      }
    }
    // Cuando NO existe categoría
    else {
      setSelectCategories(true);
    }

    return null; // Todo el flujo es válido y sin errores
  };

  const saveFile = async () => {
    try {
      const { brand, model, year, vehicle_type, serial, plate, color, weight, category } = formVehicle;

      const data = {
        client: user.id,
        route: 'emitirAuto',
        codprod: 'RCVD',
        codplan: '005',
        revplan: '001',
        tipoid: 'V',
        numid: user.dni, // CÉDULA
        dvid: 0,
        nomter: user.name || '', // NOMBRES
        apeter: user.last_name || '', // APELLIDOS
        fecnac: user.birthdate ? moment(user.birthdate).format('DD/MM/YYYY') : '',
        sexo: user.gender_id === Constants.GENDER.MALE ? 'M' : 'F', // SEXO: M/F
        email: user.email, // EMAIL
        telofi: user.phone || '', // Tlf contacto
        codpais: '001',
        codestado: '', // Consultar en Backend
        codciudad: '', // Consultar en Backend
        codmunicipio: '', // Consultar en Backend
        codparroquia: '',
        codsect: '',
        direccion: user.address || '',
        marca: formVehicle?.brand, // Consultar en Backend 
        modelo: formVehicle?.model, // Consultar en Backend
        version: '',
        claseVehiculos: '000002',
        anmoveh: formVehicle?.year, // Año del vehículo
        placa: formVehicle?.plate, // Placa
        serialCarroceria: formVehicle?.serial, // Serial carrocería
        serialMotor: formVehicle?.engine_serial, // Serial motor
        clase: '',
        color: formVehicle?.color || 'BLANCO', // Color
        kilometraje: formVehicle?.mileage, // Kilometraje
        indnuevo: 'N', // Indicador si es nuevo el vehículo (N)
        fechaAdq: "10/06/2022", // Fecha de adquisición (dd/mm/yyyy)
        tipocar: '',
        tipodes: '',
        zipecode: '2101',
        peso: formVehicle?.weight || '450', //Peso del vehiculo 
        coddestinado: "P", //Destino del Vehículo P (particular), C (carga)
        codcobro: 2, //2: WEBAPP
        //payment_id: item.payment_id, //AQUI NO SE HA PAGADO AÚN,
        user_data: {
           gender_id: form?.gender_id,
           dni: form?.dni,
           birthdate: form.birthdate ? moment(form.birthdate).format('YYYY-MM-DD') : '',
           address: form?.address,
           codestado: form?.codestado,
           codciudad: form?.codciudad,
           codmunicipio: form?.codmunicipio,
           parish_id: form?.parish_id,
           dniType: form?.dniType
        },
        selectedPriceId: request?.products[0].selectedPriceId, //ID del producto RCV a comprar
      };

      if (!brand || !model || !year || !vehicle_type || !serial || !plate || !color || !weight || (!category && selectCategories)) {
        Alert.error("Por favor, ingrese todos los datos para continuar",target);
        return;
      }

      const validationError = validateVehicleCategory({
        category,
        weight
      });
      if (validationError) {
        return validationError;
      }

      //await PurchasesService.validateVehicleData(formVehicle);
      // try {
      //   setIsLoadingData(true);

      //   //Llamo al API que emite la poliza
      //   const emissionResponse = await GeneralService.acxelCallApi(data);
      //   console.log('emissionResponse:', emissionResponse);
      // } catch (error) {
      //   console.log(`Error: ${error}`);

      //   Alert.error(`Error: ${error}`,target);
      //   return;
      // }
      // finally{
      //   setIsLoadingData(false);
      // }

      const items = request.products;
      items[dataPic.prodIdx].beneficiaries[dataPic.benefIdx].required_docs[dataPic.docIdx].file = previewImage;
      items[dataPic.prodIdx].beneficiaries[dataPic.benefIdx].required_docs[dataPic.docIdx].fileBlob = URL.createObjectURL(previewImage);
      items[dataPic.prodIdx].beneficiaries[dataPic.benefIdx].required_docs[dataPic.docIdx].formVehicle = JSON.stringify(formVehicle);
      setFormVehicle({ 
        brand: '',
        model: '',
        year: '',
        vehicle_type: '',
        serial: '',
        plate: '',
        engine_serial: '',
        mileage: '100',
        color: '',
        weight: '',
        category: ''
      });
      dispatch({ type: 'SET_REQUEST', payload: { ...request, products: items } });
      setDataPic(null);
      setVisibleVehicleData(false);
      setPreviewImage(null);
    } catch (error) {
      console.log(error);
    }
  }

  const isSubmitEnabled = isValidForm(false);

  // Verificar si falta algún dato de ubicación: Si falta alguno, le pido que llene todos los campos
  const showLocationFields = 
    !user.address || 
    !user.codestado || 
    !user.codciudad || 
    !user.codmunicipio;

  return (
    <div className="w-full lg:w-[30rem] bg-white rounded-xl overflow-hidden py-2 px-3 flex flex-col items-center">
      <div className="w-full">
        <button
          className="absolute top-3 right-3"
          onClick={() => onClose()}
        >
          <img src={CloseIcon} className="w-4 h-4 object-contain" alt="" />
        </button>
        <p className="font-semibold text-lg text-black">
          { visibleVehicleData ? 'Completar datos' : 'Datos Requeridos' }
        </p>
        {
          !visibleVehicleData && (
            <>
              {((!user.dni_file && !user.fiscal_dni_file) ||
                 !user.gender_id ||
                 !user.dni ||
                 !user.birthdate ||
                 !user.address ||
                 !user.codestado ||
                 !user.codciudad ||
                 !user.codmunicipio) &&
              (
                <p className="mt-2">
                  Dado que es tu primera compra con nosotros necesitamos algunos datos
                </p>
              )}
            </>
          )
        }
      </div>

      {
        visibleVehicleData ? (
          <div className="items-left">
            <div className="relative">
              <img src={ URL.createObjectURL(previewImage) } className="max-w-[100%] w-[600px] h-[300px] object-cover" />
              {
                attemptsAvailable != null && attemptsAvailable > 0 && !isLoadingData && (
                  <DocUploadDocument
                    button={
                      <div className="bg-blue rounded-full mt-1 py-1 px-2 font-semibold text-white flex items-center absolute bottom-3 right-3">
                        <img className="h-5 w-5 object-contain mr-2" src={ RestartIcon } />
                        <span>Reintentar</span>
                      </div>
                    }
                    onUpload={ (file) => {
                      setPreviewImage(file);
                      getData(file);
                    } }
                  />
                )
              }
            </div>
            {
              attemptsAvailable != null && attemptsAvailable <= 0 && (
                <p className="font-bold mb-3 mt-3 text-blue">Ha alcanzado la cantidad máxima de intentos. Por favor, llene los campos manualmente.</p>
              )
            }
            <p className="font-semibold mb-3 mt-3">Para continuar, por favor verifica que los datos esten correctos o vuelva a tomar la foto</p>

            { isLoadingData ? (
                <div className="text-center">
                  <div style={{ display: 'inline-flex' }}>
                    <Spinner />
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex flex-row items-center">
                    <div className="w-2/3">
                      <p className="font-semibold mb-1 mt-2">Marca</p>
                      <Input
                        // placeholder="Marca"
                        value={formVehicle.brand}
                        onValueChange={v => setFormVehicle(s => ({ ...s, ['brand']: v.toUpperCase() }))}
                      />
                    </div>
                    <div className="w-1/3 pl-4">
                      <p className="font-semibold mb-1 mt-2">Placa</p>
                      <Input
                        // placeholder="Placa"
                        value={formVehicle.plate}
                        onValueChange={v => setFormVehicle(s => ({ ...s, ['plate']: v.toUpperCase() }))}
                      />
                    </div>
                  </div>
                  <div className="flex flex-row items-center">
                    <div className="w-2/3">
                      <p className="font-semibold mb-1 mt-2">Modelo</p>
                      <Input
                        // placeholder="Modelo"
                        value={formVehicle.model}
                        onValueChange={v => setFormVehicle(s => ({ ...s, ['model']: v.toUpperCase() }))}
                      />
                    </div>
                    <div className="w-1/3 pl-4">
                      <p className="font-semibold mb-1 mt-2">Año</p>
                      <Input
                        // placeholder="Año"
                        value={formVehicle.year}
                        onValueChange={v => {
                          if ((Globals.validateInteger(v) || v == '') && v.length <= 4) {
                            setFormVehicle(s => ({ ...s, ['year']: v.toUpperCase() }));
                          }
                        }}
                      />
                    </div>
                  </div>
                  <div className="flex flex-row items-center">
                    <div className="w-2/3">
                      <p className="font-semibold mb-1 mt-2">Serial N.I.V.</p>
                      <Input
                        // placeholder="Serial N.I.V."
                        value={formVehicle.serial}
                        onValueChange={v => setFormVehicle(s => ({ ...s, ['serial']: v.toUpperCase() }))}
                      />
                    </div>
                    <div className="w-1/3 pl-4">
                      <p className="font-semibold mb-1 mt-2">Cap. Carga KGS</p>
                      <Input
                        // placeholder="Cap. Carga KGS"
                        value={formVehicle.weight}
                        onValueChange={v => {
                          if ((Globals.validateInteger(v) || v == '') && v.length <= 4) {
                            setFormVehicle(s => ({ ...s, ['weight']: v.toUpperCase() }));
                          }
                        }}
                      />
                    </div>
                  </div>
                  <div className="flex flex-row items-center">
                    <div className="w-[50%]">
                      <p className="font-semibold mb-1 mt-2">Tipo de Vehículo</p>
                      <Input
                        // placeholder="Tipo de Vehículo"
                        value={formVehicle.vehicle_type}
                        onValueChange={v => setFormVehicle(s => ({ ...s, ['vehicle_type']: v.toUpperCase() }))}
                      />
                    </div>
                    <div className="w-[50%] pl-4">
                      <p className="font-semibold mb-1 mt-2 text-left">Color</p>
                      <Input
                        // placeholder="Color"
                        value={formVehicle.color}
                        onValueChange={v => setFormVehicle(s => ({ ...s, ['color']: v.toUpperCase() }))}
                      />
                    </div>
                  </div>
                  {
                    selectCategories && (
                      <div className="flex flex-row items-center">
                        <div className="w-[100%]">
                          <p className="font-semibold mb-1 mt-2">Clase</p>
                          <Select
                            items={Object.values(Constants.BRAVEALO.CATEGORY).map((item) => {
                              return {
                                label: item,
                                value: item
                              }
                            })}
                            value={formVehicle.category}
                            onValueChange={v => setFormVehicle(s => ({ ...s, ['category']: v }))}
                          />
                        </div>
                      </div>
                    )
                  }  
                  <div className="text-center">
                    <button
                      className="rounded-lg w-60 text-center h-10 font-bold text-lg my-2 disabled:bg-gray6 disabled:text-gray3 bg-yellow text-black mt-7 mb-5"
                      onClick={saveFile}
                    >
                      <span>Guardar</span>
                    </button>
                  </div>
                </>
              )
            }
          </div>
        ) : (
          <>
            <div className="w-full mt-4">
              {!user.dni_file && (
                <DocUploadItem
                  label="Cédula de identidad"
                  file={user.dni_file ? fromPhotos(user.dni_file) : form.dniFile}
                  fileUrl={user.dni_file ? fromPhotos(user.dni_file) : form.dniFileBlob}
                  note="Del titular"
                  onUpload={file => selectFile(file, 'dniFile')}
                />
              )}

              {
                !user.gender_id && (
                  <Select
                    placeholder="Sexo"
                    style={{ marginBottom: 20 }}
                    items={genders}
                    value={form.gender_id}
                    onValueChange={v => setForm(s => ({ ...s, ['gender_id']: v }))}
                  />
                )
              }

              {
                !user.dni && (
                 <div className="flex flex-row items-center mt-3">
                    <div className="w-1/3">
                      <Select
                        style={{ flex: 3 }}
                        items={documentTypes}
                        value={form.dniType}
                        onValueChange={v => setForm(s => ({ ...s, ['dniType']: v }))}
                      />
                    </div>
                    <div className="w-2/3 pl-4">
                      <Input
                        style={{ flex: 5 }}
                        placeholder="Cédula"
                        keyboardType="number-pad"
                        value={form.dni}
                        onValueChange={v => setForm(s => ({ ...s, ['dni']: sanitizeToNumbers(v) }))}
                        maxLength={9}
                      />
                    </div>
                  </div>
                )
              }

              {
                !user.birthdate && (
                  <div className="mt-3">
                    <Input
                      type="date"
                      placeholder="Fecha de nacimiento"
                      value={form.birthdate}
                      onValueChange={v => setForm(s => ({ ...s, ['birthdate']: v }))}
                    />
                  </div>
                )
              }

                {showLocationFields && (
                  <>
                    <div className="mt-3">
                      <Input
                        placeholder="Dirección"
                        value={form.address}
                        onValueChange={v => setForm(s => ({ ...s, address: v }))}
                      />
                    </div>

                    <div className="mt-3">
                      <Select
                        placeholder="Estado"
                        style={{ marginBottom: 20 }}
                        items={states}
                        value={form.codestado}
                        onValueChange={v => {
                          setForm(s => ({ ...s, codestado: String(v), codmunicipio: '', parish_id: '' }));
                          //getTownships(v);
                          getAcselCities(v);
                        }}
                      />
                    </div>

                    <div className="mt-3">
                      <Select
                        placeholder="Ciudad"
                        style={{ marginBottom: 20 }}
                        items={cities}
                        value={form.codciudad}
                        //onValueChange={v => setForm(s => ({ ...s, codciudad: v }))}
                        onValueChange={v => {
                          setForm(s => ({ ...s, codciudad: String(v) }));
                          getAcselTownships(v);
                        }}
                      />
                    </div>

                    <div className="mt-3">
                      <Select
                        placeholder="Municipio"
                        style={{ marginBottom: 20 }}
                        items={townships}
                        value={form.codmunicipio}
                        onValueChange={v => {
                          setForm(s => ({ ...s, codmunicipio: String(v), parish_id: '' }));
                          //getParishes(v);
                        }}
                      />
                    </div>
                  </>
                )}

              {/* {!user.fiscal_dni_file && (
                <DocUploadItem
                  label="RIF"
                  file={user.fiscal_dni_file ? fromPhotos(user.fiscal_dni_file) : form.fiscalDniFile}
                  fileUrl={user.fiscal_dni_file ? fromPhotos(user.fiscal_dni_file) : form.fiscalDniFileBlob}
                  onClick={file => selectFile(file, 'fiscalDniFile')}
                />
              )} */}

              {request?.products?.map((item, prodIdx) => {
                if (!item?.product?.required_docs?.length) return null;

                return item?.beneficiaries?.map((benef, benefIdx) => (
                  <div key={`${prodIdx}-${benefIdx}`}>
                    <p className="w-full mb-2 text-darkBlue font-semibold text-sm text-left">
                      { item?.product?.name } - { benef?.fullName }
                    </p>
                    {clone(item?.product?.required_docs)?.sort((a,b) => a.is_optional - b.is_optional || b.bravealo - a.bravealo).map((x) => {
                      const docIdx = item?.product?.required_docs.findIndex(i => i.id == x.id);
                      return (
                        <DocUploadItem
                          beneficiary={ true }
                          key={`${prodIdx}-${benefIdx}-${docIdx}`}
                          label={x.name}
                          file={benef?.required_docs[docIdx]?.file}
                          fileUrl={benef?.required_docs[docIdx]?.fileBlob}
                          isOptional={x.is_optional && !x.bravealo}
                          form={ form }
                          onPressSame={() => {
                            const items = clone(request.products);
                            items[prodIdx].beneficiaries[benefIdx].required_docs[docIdx].file = form.dniFile;
                            items[prodIdx].beneficiaries[benefIdx].required_docs[docIdx].fileBlob = URL.createObjectURL(form.dniFile);
                            dispatch({ type: 'SET_REQUEST', payload: { ...request, products: items } });
                          }}
                          disabled={ 
                            x.bravealo &&
                            ((!form.gender_id && !user.gender_id) ||
                            (!form.dniType && !user.dni) ||
                            (!form.dni && !user.dni) ||
                            (!form.birthdate && !user.birthdate) ||
                            (!form.address && !user.address) || 
                            (!form.codestado && !user.codestado) || 
                            (!form.codciudad && !user.codciudad) || 
                            (!form.codmunicipio && !user.codmunicipio))
                          }
                          onClickDisabled={ () => {
                            if (x.bravealo) {
                              if (!form.gender_id && !user.gender_id)
                                return Alert.error('Para continuar con el certificado, debe llenar el campo sexo',target);

                              if (!form.dniType && !user.dni)
                                return Alert.error('Para continuar con el certificado, debe llenar el campo tipo de documento',target);

                              if (!form.dni && !user.dni)
                                return Alert.error('Para continuar con el certificado, debe llenar el campo número de documento',target);

                              if (!form.birthdate && !user.birthdate)
                                return Alert.error('Para continuar con el certificado, debe llenar la fecha de nacimiento',target);

                              if (!form.address && !user.address)
                                return Alert.error('Para continuar con el certificado, debe llenar el campo dirección',target);

                              if (!form.codestado && !user.codestado)
                                return Alert.error('Para continuar con el certificado, debe llenar el campo estado',target);

                              if (!form.codciudad && !user.codciudad)
                                return Alert.error('Para continuar con el certificado, debe llenar el campo ciudad',target);

                              if (!form.codmunicipio && !user.codmunicipio)
                                return Alert.error('Para continuar con el certificado, debe llenar el campo municipio',target);

                              // if (!form.parish_id && !user.parish_id)
                              //   return Alert.error('Para continuar con el certificado, debe llenar el campo parroquia',target);
                            }
                          } }
                          onUpload={file => {
                            if (x.bravealo) {
                              setVisibleVehicleData(true);
                              setPreviewImage(file);
                              setDataPic({
                                prodIdx, docIdx, benefIdx
                              });
                              getData(file);
                            }
                            else {
                              uploadDoc(file, prodIdx, docIdx, benefIdx);
                            }
                          }}
                        />
                      )
                    })}
                  </div>
                ));
              })}
            </div>

            <div className="w-full flex justify-center">
              {isLoading ? (
                <Spinner />
              ) : (
                <button
                  className={classNames(
                    'rounded-lg w-60 text-center h-10 font-bold my-2 disabled:bg-gray6 disabled:text-gray3',
                    isSubmitEnabled ? 'bg-yellow text-black' : 'text-gray bg-gray6'
                  )}
                  onClick={submit}
                  disabled={!isSubmitEnabled}
                >
                  <span>Continuar</span>
                </button>
              )}
            </div>
          </>
        )
      }
    </div>
  );
};

const DocUploadItem = ({ file, fileUrl, label, isOptional, onUpload, note, beneficiary, onPressSame, form, disabled, onClickDisabled }) => {
  let icon = UploadFileIcon;
  if (file) {
    icon = fileUrl;
    if (file?.type === 'application/pdf' || String(file).endsWith('pdf')) icon = PDFIcon;
  }
  const is_cedula = clean(label).toUpperCase().includes(clean(STRING_CEDULA).toUpperCase());
  const user = useSelector(state => state.user);

  if (is_cedula && beneficiary) {
    note = 'Del beneficiario';
  }

  return (
    <>
      <div className="border border-gray6 rounded-xl mb-3">
        <label className="w-full flex justify-between items-center cursor-pointer p-2" onClick={ () => {
          if (disabled && onClickDisabled) {
            onClickDisabled();
          }
        } }>
          <div className="flex flex-1 justify-start items-center">
            <div
              className={classNames(
                'w-12 h-12 flex justify-center items-center mr-3 rounded-md',
                icon === UploadFileIcon ? 'bg-darkBlue' : 'bg-white'
              )}
            >
              {icon === UploadFileIcon ? (
                <img src={icon} key="placeholder" className="w-6 h-6 object-contain" alt="" />
              ) : (
                <img src={icon} key="file" className="w-12 h-12 object-cover" alt="" />
              )}
            </div>
            <div className="flex-1">
              <p className="flex-1 text-left text-sm">
                { label } { isOptional ? '(opcional)' : '' }
              </p>
              {
                note != null && (
                  <p className="flex-1 text-red font-bold text-left text-xs">
                    { note }
                  </p>
                )
              }
            </div>
          </div>
          <img src={ChevronRightIcon} className="w-5 h-5 object-contain" alt="" />

          {
            !disabled && (
              <input
                type="file"
                className="hidden"
                accept="application/pdf,image/jpeg,image/png"
                onChange={e => onUpload(e.target.files[0])}
              />
            )
          }
        </label>
        {
          !user.dni_file && form?.dniFile != null && icon === UploadFileIcon && beneficiary && is_cedula && (
            <button className="rounded-full text-center text-xs text-white font-bold bg-darkBlue p-1 mb-2 ml-2" onClick={ onPressSame }>
               Usar misma cédula del titular
            </button>
          )
        }
      </div>
    </>
  );
};
